import { useCallback, useMemo, useReducer } from 'react';
import { parseAmount } from './refundFormat';

// Ephemeral per-line selection state for the refund dialog: amounts keyed by `ref` and an
// `active` flag per checked line, plus the derived totals, per-rate usage vs the caps, the
// VAT preview and the POST payload. `segmentLabel` is injected so the hook stays pure (the
// label needs the translate HOC, which lives in the components).

const EPSILON = 1e-9;

const toNumber = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const initialState = { amounts: {}, active: {} };

// Drop a line from the selection and clear its amount.
const deactivate = (state, ref) => {
  const active = { ...state.active };
  delete active[ref];
  const amounts = { ...state.amounts };
  delete amounts[ref];
  return { active, amounts };
};

const selectionReducer = (state, action) => {
  switch (action.type) {
    case 'SET_AMOUNT':
      return { ...state, amounts: { ...state.amounts, [action.ref]: action.amount } };
    case 'TOGGLE_LINE': {
      const { ref, originalPrice, decimals } = action;
      if (state.active[ref]) return deactivate(state, ref);
      // Activate. Pre-fill the original price as a sensible default (editable).
      const active = { ...state.active, [ref]: true };
      const amounts = { ...state.amounts };
      if (originalPrice > 0) amounts[ref] = originalPrice.toFixed(decimals);
      return { active, amounts };
    }
    case 'DESELECT_LINE':
      return deactivate(state, action.ref);
    case 'CLEAR_ALL':
      return initialState;
    default:
      return state;
  }
};

export const useRefundSelection = (
  items = [],
  { decimals = 2, refundableAmount, refundableByRate = [], segmentLabel = name => name } = {}
) => {
  const [{ amounts, active }, dispatch] = useReducer(selectionReducer, initialState);

  // Original price per ref, derived from items only (independent of amounts), so
  // toggling can pre-fill without depending on the rendered lines.
  const priceByRef = useMemo(() => {
    const map = new Map();
    items.forEach(item => map.set(item.ref, toNumber(item.originalPrice)));
    return map;
  }, [items]);

  // Per-rate caps keyed by normalized numeric rate. Validation-only; entries are per
  // single rate and must never be summed (a multi-tax line hits each of its rates).
  const capByRate = useMemo(() => {
    const map = new Map();
    (refundableByRate || []).forEach(({ taxRate, refundableAmount: amount }) => {
      map.set(toNumber(taxRate), toNumber(amount));
    });
    return map;
  }, [refundableByRate]);

  const setAmount = useCallback((ref, amount) => {
    // Fully clearing a line deselects it, so "N selected" only counts lines carrying
    // an amount. A typed "0" is kept (decimals like 0.50).
    if (amount === '') {
      dispatch({ type: 'DESELECT_LINE', ref });
      return;
    }
    dispatch({ type: 'SET_AMOUNT', ref, amount });
  }, []);
  const toggleLine = useCallback(
    ref => dispatch({ type: 'TOGGLE_LINE', ref, originalPrice: priceByRef.get(ref) || 0, decimals }),
    [priceByRef, decimals]
  );
  const clearAll = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), []);

  const cap = toNumber(refundableAmount);

  const derived = useMemo(() => {
    // One row per fiscal line (mono- or multi-tax).
    const lines = items.map(item => {
      const raw = amounts[item.ref];
      const amount = parseAmount(raw);
      const selected = raw !== undefined && raw !== '' && amount > 0;
      const rates = (item.taxRates || []).map(toNumber);
      const originalPrice = toNumber(item.originalPrice);
      return {
        item,
        ref: item.ref,
        name: item.name,
        label: segmentLabel(item.name),
        rates,
        multiTax: !!item.multiTax || rates.length > 1,
        originalPrice,
        rawAmount: raw || '',
        amount,
        selected,
        isActive: !!active[item.ref],
        // Soft, non-blocking signal: the amount exceeds this line's (pre-discount) gross.
        isOverOriginal: originalPrice > 0 && amount > originalPrice + EPSILON,
      };
    });

    const selectedLines = lines.filter(l => l.selected);
    const selectedTotal = selectedLines.reduce((sum, l) => sum + l.amount, 0);
    const isOverBudget = selectedTotal > cap + EPSILON;

    // Per-rate usage: a line's FULL amount counts on EACH of its rates, matching the
    // BE per-rate validation. Compared to refundableByRate to pre-validate before POST.
    const usageByRate = new Map();
    selectedLines.forEach(({ amount, rates }) => {
      rates.forEach(rate => usageByRate.set(rate, (usageByRate.get(rate) || 0) + amount));
    });
    const overRates = [];
    usageByRate.forEach((used, rate) => {
      if (capByRate.has(rate) && used > capByRate.get(rate) + EPSILON) {
        overRates.push({ rate, used, cap: capByRate.get(rate) });
      }
    });
    overRates.sort((a, b) => b.rate - a.rate);
    // Refs of selected lines touching an over-cap rate (drives the row-level error).
    const overRateSet = new Set(overRates.map(o => o.rate));
    const overRefs = new Set(selectedLines.filter(l => l.rates.some(r => overRateSet.has(r))).map(l => l.ref));

    // Live VAT preview. Mono-tax lines split TTC-first per rate. Multi-tax lines can't
    // be split by the FE (shared base), so they bucket separately (rates + TTC only).
    const byRateMap = new Map();
    const multiTaxMap = new Map();
    selectedLines.forEach(line => {
      if (line.multiTax) {
        const key = line.rates.join('_');
        const bucket = multiTaxMap.get(key) || { rates: line.rates, ttc: 0 };
        bucket.ttc += line.amount;
        multiTaxMap.set(key, bucket);
      } else {
        const rate = line.rates[0] || 0;
        const group = byRateMap.get(rate) || { rate, ttc: 0 };
        group.ttc += line.amount;
        byRateMap.set(rate, group);
      }
    });
    const byRate = [...byRateMap.values()]
      .sort((a, b) => b.rate - a.rate)
      .map(({ rate, ttc }) => {
        const ht = ttc / (1 + rate);
        return { rate, ttc, ht, vat: ttc - ht };
      });
    const multiTax = [...multiTaxMap.values()].sort((a, b) => (b.rates[0] || 0) - (a.rates[0] || 0));

    // HT/VAT totals cover only the splittable (mono-tax) lines; multi-tax contributes
    // TTC only. `totalTTC` is the true grand total across both.
    const monoTTC = byRate.reduce((sum, g) => sum + g.ttc, 0);
    const monoHT = byRate.reduce((sum, g) => sum + g.ht, 0);
    const multiTTC = multiTax.reduce((sum, g) => sum + g.ttc, 0);

    return {
      lines,
      // Display count = checked rows; the Confirm gate uses amount-based hasSelection.
      selectedCount: Object.keys(active).length,
      selectedTotal,
      refundableAmount: cap,
      remaining: cap - selectedTotal,
      isOverBudget,
      overRates,
      overRefs,
      hasSelection: selectedLines.length > 0,
      isValid: selectedLines.length > 0 && !isOverBudget && overRates.length === 0,
      summary: {
        byRate,
        multiTax,
        totalHT: monoHT,
        totalVAT: monoTTC - monoHT,
        totalTTC: monoTTC + multiTTC,
        hasMultiTax: multiTax.length > 0,
      },
      // Body for POST /refund: one { ref, amount } per selected line (a multi-tax
      // line is a single entry carrying its combined ref).
      payloadItems: selectedLines.map(({ ref, amount }) => ({ ref, amount: amount.toFixed(decimals) })),
    };
  }, [items, amounts, active, cap, capByRate, decimals, segmentLabel]);

  return { ...derived, setAmount, toggleLine, clearAll };
};
